import { useEffect, useMemo, useState } from "react";

import styles from "styles/pages/reports/reports.module.scss";

import * as echarts from "echarts";

import classNames from "classnames/bind";
import ComboFilters from "../../ComboFilters";
import { If } from "shared/components/control-statements";
import { Hairline, Loading } from "@components";
import { useDragScroll } from "shared/hooks/useDragScroll";
import { OlgaEnvironmentsAPI } from "providers/OlgaEnvironmentsAPI";
import { formatByType, Trend } from "pages/Reports/Reports";
import { Numbers } from "shared/utils/Numbers";
import { useDrag } from "shared/hooks/useDrag";

const css = classNames.bind(styles);

const filters = [
  { name: "Taxa de uso", key: "use_ratio", type: "percentage" },
  { name: "Taxa de expiração", key: "expired_ratio", type: "percentage" },
  { name: "Tempo médio para uso", key: "average_use_time", type: "int" },
  { name: "ROI do cupom", key: "roi", type: "int" },
];

const getFilterName = (key) => {
  const filter = filters.find((f) => f.key === key);
  return filter ? filter.name : key;
};

const getFilterType = (key) => {
  const filter = filters.find((f) => f.key === key);
  return filter ? filter.type : "string";
};

const getFormatedValue = (key, value) => {
  return formatByType(value, getFilterType(key));
};

export default function IndicatorsDetails({ data = {}, hasReportFilters }) {
  const [showAll, setShowAll] = useState("see_details");
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState("use_ratio");
  const [label_dict, setLabelDict] = useState(new Map());
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    const response_units = await OlgaEnvironmentsAPI.getUnits();
    const response_brands = await OlgaEnvironmentsAPI.getBrands();

    const units = response_units.getData([]);
    const brands = response_brands.getData([]);

    setLabelDict(
      new Map([
        ...units.map((unit) => [`units_${unit.id}`, unit.name]),
        ...brands.map((brand) => [`brands_${brand.id}`, brand.name]),
      ])
    );

    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [data]);

  const memoData = useMemo(() => data, [JSON.stringify(data)]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const chartDOM = document.getElementById("indicators-details-chart");
      if (!chartDOM) return;

      const chart = echarts.init(chartDOM, null, {
        renderer: "svg",
      });

      const getChartData = () => {
        const distribution = (data?.[selectedOption?.id || "units"] || [])?.map(
          (item) => ({
            name:
              label_dict.get(
                `${selectedOption?.id || "units"}_${
                  item.unit_id || item.brand_id
                }`
              ) ||
              item.unit_id ||
              item.brand_id,
            value: item[selectedFilter]?.value || 0,
          })
        );

        return distribution;
      };

      const options = {
        tooltip: {
          trigger: "item",
          responsive: true,
          formatter: (param) => {
            const data = param.data;
            return [
              `<span>${data.name}</span>
							<br/>
							<span style="opacity: 50%">
								${getFormatedValue(selectedFilter, data.value)}								
							</span>
						`,
            ].join("");
          },
          backgroundColor: "#242424",
          borderWidth: "0",
          borderColor: "transparent",
          textStyle: {
            color: "#fff",
            fontSize: 10,
          },
        },
        series: [
          {
            type: "pie",
            radius: ["75%"],
            center: ["50%", "45%"],
            label: {
              show: false,
              position: "center",
            },
            data: getChartData(),
          },
        ],
        legend: {
          type: "scroll",
          orient: "horizontal",
          pageIconColor: "#6D6DF2",
          pageIconInactiveColor: "#4C4C4C",
          backgroundColor: "#1C1C1C",
          borderRadius: 10,
          padding: 20,
          pageIconSize: 10,
          pageTextStyle: {
            color: "#B3B3B3",
            fontSize: 10,
            fontWeight: 800,
          },
          pageIcons: {
            horizontal: [
              "path://M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6z",
              "path://M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z",
            ],
          },
          top: "bottom",
          icon: "circle",
          itemWidth: 8,
          itemHeight: 8,
          itemGap: 10,
          textStyle: {
            color: "#808080",
            fontSize: 12,
          },
          inactiveColor: "#555",
          selected: Object.fromEntries(
            getChartData().map((item, index) => [item.name, index < 5])
          ),
        },
      };

      options && chart.setOption(options);

      const ro = new ResizeObserver(() => {
        chart.resize();
      });
      ro.observe(chartDOM);

      return () => {
        ro.unobserve(chartDOM);
        ro.disconnect();
        chart.dispose();
      };
    }
  }, [memoData, selectedOption, selectedFilter, showAll, label_dict]);

  const { scrollRef, onMouseDown } = useDragScroll();

  const sortByOrderQuantity = (arr) =>
    arr.sort(
      (a, b) =>
        Number(b.order_quantity?.value) - Number(a.order_quantity?.value)
    );

  data.brands = sortByOrderQuantity(data?.brands || []);
  data.units = sortByOrderQuantity(data?.units || []);

  const scrollFiltersRef = useDrag(null);

  return (
    <div className={styles.box2} style={{ minHeight: 632 }}>
      <div className="d-flex align-center justify-between px-20">
        <div className="d-flex align-center gap-5">
          <p className={styles.label}>
            Detalhamento de indicadores de fidelização
          </p>
          <span
            className={styles.info}
            title="Distribuição dos dados de cashback por unidade, marca e origem."
          >
            <i className="fal fa-info-circle" />
          </span>
        </div>
        <div className="d-flex align-center">
          <div className="d-flex align-center gap-10">
            <div
              className={css({
                selectMini: true,
                selected: showAll === "see_details",
              })}
              onClick={() => setShowAll("see_details")}
            >
              Ver detalhes
            </div>
            <div
              className={css({
                selectMini: true,
                selected: showAll === "see_all",
              })}
              onClick={() => setShowAll("see_all")}
            >
              Ver tudo
            </div>
          </div>
        </div>
      </div>

      <If cond={loading}>
        <Loading fullContent />
      </If>

      <If cond={showAll === "see_details"}>
        <div className="mb-15 mt-15">
          <div className={styles.boxFilters} ref={scrollFiltersRef}>
            {filters.map((filter) => (
              <div
                key={filter.key}
                className={css({
                  filter: true,
                  selected: selectedFilter === filter.key,
                })}
                onClick={() => setSelectedFilter(filter.key)}
              >
                {filter.name}
              </div>
            ))}
          </div>
        </div>
      </If>

      <If cond={showAll === "see_all"}>
        <Hairline className="mt-15" />
      </If>

      <div className={styles.container}>
        <If cond={showAll === "see_details"}>
          <div className="px-20 d-flex w-100">
            <div
              className={styles.tableOrdersDistribution}
              onMouseDown={onMouseDown}
              ref={scrollRef}
            >
              <table>
                <thead>
                  <tr>
                    <th>
                      <div className="d-flex">
                        <ComboFilters
                          options={[
                            { name: "Unidades", id: "units" },
                            { name: "Marcas", id: "brands" },
                          ]}
                          design="two"
                          onSelect={(option) => setSelectedOption(option)}
                          selectedOption={
                            selectedOption || {
                              name: "Unidades",
                              id: "Unidades",
                            }
                          }
                        />
                      </div>
                    </th>
                    <th>{getFilterName(selectedFilter || "use_ratio")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.[selectedOption?.id || "units"]?.map((item, index) => {
                    return (
                      <tr key={index}>
                        <td>
                          {label_dict.get(
                            `${selectedOption?.id || "units"}_${
                              item.unit_id || item.brand_id
                            }`
                          ) || item.unit_id | item.brand_id}
                        </td>
                        <td>
                          <div className="d-flex align-center">
                            <p className={styles.value}>
                              {getFormatedValue(
                                selectedFilter,
                                item[selectedFilter]?.value
                              )}
                            </p>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className={styles.chartContainer}>
              <p className={styles.title}>Proporção de Taxa de uso</p>
              <div
                id="indicators-details-chart"
                style={{
                  width: "100%",
                  height: "100%",
                  margin: "auto",
                }}
              />
            </div>
          </div>
        </If>

        <If cond={showAll === "see_all"}>
          <div
            className={styles.tableOrdersDistributionFull}
            onMouseDown={onMouseDown}
            ref={scrollRef}
          >
            <table>
              <thead>
                <tr>
                  <th
                    style={{
                      position: "sticky",
                      left: 0,
                      zIndex: 200,
                      backgroundColor: "#1f1f1f",
                    }}
                  >
                    <div className="d-flex">
                      <ComboFilters
                        options={[
                          { name: "Unidades", id: "units" },
                          { name: "Marcas", id: "brands" },
                        ]}
                        design="two"
                        onSelect={(option) => setSelectedOption(option)}
                        selectedOption={
                          selectedOption || {
                            name: "Unidades",
                            id: "units",
                          }
                        }
                      />
                    </div>
                  </th>
                  <th style={{ paddingLeft: 35 }}>TAXA DE USO</th>
                  <th style={{ paddingLeft: 35 }}>TAXA DE EXPIRAÇÃO</th>
                  <th style={{ paddingLeft: 35 }}>TEMPO MÉDIO PARA USO</th>
                  <th style={{ paddingLeft: 35 }}>ROI DO CUPOM</th>
                </tr>
              </thead>
              <tbody>
                {data?.[selectedOption?.id || "units"]?.map((item, index) => {
                  return (
                    <tr key={index}>
                      <td
                        style={{
                          position: "sticky",
                          left: 0,
                          zIndex: 3,
                          backgroundColor: "#1c1c1c",
                        }}
                      >
                        {label_dict.get(
                          `${selectedOption?.id || "units"}_${
                            item.unit_id || item.brand_id
                          }`
                        ) || item.unit_id | item.brand_id}
                      </td>
                      <td>
                        <div
                          className="d-flex align-center"
                          style={{ paddingLeft: 20 }}
                        >
                          <p className={styles.value}>
                            {Numbers.formatPercent(item.use_ratio.value)}
                          </p>
                        </div>
                      </td>
                      <td>
                        <div
                          className="d-flex align-center"
                          style={{ paddingLeft: 20 }}
                        >
                          <p className={styles.value}>
                            {Numbers.formatPercent(item.expired_ratio.value)}
                          </p>
                        </div>
                      </td>
                      <td>
                        <div
                          className="d-flex align-center"
                          style={{ paddingLeft: 20 }}
                        >
                          <p className={styles.value}>
                            {Numbers.formatInt(item.average_use_time.value)}{" "}
                            dias
                          </p>
                          <If cond={!hasReportFilters}>
                            <Trend growth={item.average_use_time?.growth} />
                          </If>
                        </div>
                      </td>
                      <td>
                        <div
                          className="d-flex align-center"
                          style={{ paddingLeft: 20 }}
                        >
                          <p className={styles.value}>
                            {Numbers.formatInt(item.roi.value)}
                          </p>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {Array.from({
                  length: Math.max(
                    0,
                    12 - data?.[selectedOption?.id || "units"]?.length
                  ),
                }).map((_, i) => (
                  <tr key={`empty-${i}`}>
                    <td
                      style={{
                        position: "sticky",
                        left: 0,
                        zIndex: 3,
                        backgroundColor: "#1c1c1c",
                      }}
                    >
                      &nbsp;
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </If>
      </div>
    </div>
  );
}
